import { memo, useCallback, useEffect, useRef, ComponentPropsWithoutRef } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import Icon from 'builder/components/Icon'
import { MouseClickEvent } from 'builder/modules/ui'
import { i18n as I18n } from 'builder/utils/i18n'
import { DocumentTypes } from 'builder/modules/constants'
import { navigate } from 'builder/modules/navigate'
import {
  actions as resumeEditorActions,
  AFTER_USE_EXAMPLE_HIGHLIGHT_ID,
  selectors,
} from 'builder/modules/resumeEditor'
import { ButtonTheme, ButtonV2 } from 'builder/components/Button'
import { useAiResumeReviewHighlightState } from 'builder/components/AiFeatures/AiResumeReview'
import { RbpAiReviewAbTestType } from 'builder/modules/init'
import { EditorPanelSwitcher } from 'builder/components/EditorPanelSwitcher'
import { EditorPanelType } from 'builder/modules/generalEditor'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import DownloadMenuLayout from '../DownloadMenuLayout'
import { TooltipInfoWrapper } from '../TooltipInfoWrapper/TooltipInfoWrapper'
import { CurrentDocument } from '../../types'
import {
  Topbar,
  SelectTemplateButtonContainer,
  ButtonContainerRight,
  SelectTemplateButton,
} from './styles'

type Props = ComponentPropsWithoutRef<typeof Topbar> & {
  type: DocumentTypes
  currentDocument: CurrentDocument
  documentId: number
  width?: number
  isDownloading: boolean
  handleFullSize: (event?: MouseClickEvent) => void
  customAction?: () => void
  customDownloadAction?: () => void
  showResumeDistributionModal?: () => void
  triggerDownload?: boolean
  isRbpStepFlowActive?: boolean
  isEditorPanelTypeSwitcherEnabled?: boolean
  handleEditorPanelTypeChange?: (type: EditorPanelType) => void
  editorPanelType?: EditorPanelType
  aiResumeReviewABTestVariant?: RbpAiReviewAbTestType
  shouldShowSelectTemplateButton?: boolean
}

const RESUME_REVIEW_SWITCHER_ID = 'ai_resume_review_switcher'

export const TopbarLayout = memo((props: Props) => {
  const {
    width,
    type,
    documentId,
    currentDocument,
    isDownloading,
    handleFullSize,
    customAction,
    customDownloadAction,
    showResumeDistributionModal,
    triggerDownload,
    isRbpStepFlowActive,
    isEditorPanelTypeSwitcherEnabled,
    editorPanelType = 'editor',
    handleEditorPanelTypeChange,
    aiResumeReviewABTestVariant,
    shouldShowSelectTemplateButton,
    ...rest
  } = props
  const location = useLocation()
  const dispatch = useDispatch()
  const exploreTemplateButtonRef = useRef<HTMLButtonElement>(null)
  const { isAiResumeReviewSwitcherSeen, setIsAiResumeReviewSwitcherSeen } =
    useAiResumeReviewHighlightState()
  const isResume = type === 'resume'

  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )

  const handleSelectTemplate = useCallback(
    (event: MouseClickEvent) => {
      trackInternalEvent(`click_select_${type}_template_button`)
      handleFullSize(event)
    },
    [handleFullSize, type],
  )

  const queryParams = new URLSearchParams(location.search)
  const exploreTemplates = queryParams.get('show_templates')

  const shouldShowAiReviewSwitcher =
    isEditorPanelTypeSwitcherEnabled &&
    aiResumeReviewABTestVariant &&
    aiResumeReviewABTestVariant !== 'v0'

  const customizationButtonText = isResume
    ? isRbpStepFlowActive
      ? I18n.t('builder.resume_editor.change_template')
      : I18n.t('builder.resume_editor.customization')
    : I18n.t('builder.resume_editor.select_template')

  useEffect(() => {
    if (exploreTemplates === '1') {
      const newUrlWithoutExploreParam = `${location.pathname}${location?.search?.replaceAll(
        '?show_templates=1',
        '',
      )}`
      navigate(newUrlWithoutExploreParam)
      if (exploreTemplateButtonRef.current?.click) {
        exploreTemplateButtonRef.current?.click()
      }
    }
  }, [exploreTemplates, handleSelectTemplate, location.pathname, location?.search])

  useEffect(() => {
    if (isEditorPanelTypeSwitcherEnabled && !isAiResumeReviewSwitcherSeen) {
      const translationKey = 'builder.resume_editor.ai_review_switcher_highlight'
      dispatch(
        resumeEditorActions.setHighlightedElement({
          title: I18n.t(`${translationKey}.title`),
          content: I18n.t(`${translationKey}.description`),
          target: `[data-highlight-id=${RESUME_REVIEW_SWITCHER_ID}]`,
          buttonLabel: I18n.t(`${translationKey}.ok_button_label`),
          skipButtonLabel: I18n.t(`${translationKey}.skip_button_label`),
          disableOverlayClose: true,
          disableCloseOnEsc: true,
          showSkipOnLast: true,
          nextButtonProps: {
            leadingIcon: <Icon.FeaturedRegular />,
            theme: ButtonTheme.alternative,
          },
          onClose: () => {
            handleEditorPanelTypeChange?.('ai_review')
            setIsAiResumeReviewSwitcherSeen(true)
            trackInternalEvent('click_check_with_ai', {
              label: 'ai_review_popup',
            })
          },
          onSkip: () => {
            setIsAiResumeReviewSwitcherSeen(true)
          },
        }),
      )
      trackInternalEvent('see_ai_review_popup')
    }
  }, [
    dispatch,
    isEditorPanelTypeSwitcherEnabled,
    isAiResumeReviewSwitcherSeen,
    handleEditorPanelTypeChange,
    setIsAiResumeReviewSwitcherSeen,
  ])

  if (autoTailoringFromPublicJobBoard.enabled) {
    return null
  }

  return (
    <Topbar previewWidth={width} {...rest}>
      <ButtonContainerRight
        $isInvisible={!shouldShowSelectTemplateButton}
        $invisibilityStrategy={aiResumeReviewABTestVariant === 'v2' ? 'invisible' : 'none'}
      >
        <TooltipInfoWrapper
          position="bottom"
          zIndex={11000}
          tooltip={I18n.t(`builder.resume_editor.change_template_tooltip`)}
        >
          <SelectTemplateButtonContainer data-highlight-id={AFTER_USE_EXAMPLE_HIGHLIGHT_ID}>
            {isEditorPanelTypeSwitcherEnabled ? (
              <ButtonV2
                onClick={handleSelectTemplate}
                ref={exploreTemplateButtonRef}
                leadingIcon={<Icon.Grid />}
                theme={ButtonTheme.ghost}
                size="small"
                data-testid="customization-button"
              >
                {customizationButtonText}
              </ButtonV2>
            ) : (
              <SelectTemplateButton
                onClick={handleSelectTemplate}
                ref={exploreTemplateButtonRef}
                leadingIcon={<Icon.Grid />}
                size="small"
                data-testid="customization-button"
              >
                {customizationButtonText}
              </SelectTemplateButton>
            )}
          </SelectTemplateButtonContainer>
        </TooltipInfoWrapper>
      </ButtonContainerRight>
      {shouldShowAiReviewSwitcher && (
        <ButtonContainerRight>
          <EditorPanelSwitcher
            activePanelType={editorPanelType}
            handlePanelTypeChange={handleEditorPanelTypeChange}
            theme="darker"
            isFullWidth={false}
            items={[
              {
                text: I18n.t('builder.resume_editor.preview'),
                id: 'editor',
                dataTestId: 'editor-panel-switcher-item-editor',
              },
              {
                text: I18n.t('builder.resume_editor.ai_review'),
                id: 'ai_review',
                highlightId: RESUME_REVIEW_SWITCHER_ID,
                dataTestId: 'editor-panel-switcher-item-ai-review',
              },
            ]}
            data-testid="editor-panel-switcher"
          />
        </ButtonContainerRight>
      )}
      {currentDocument && !isRbpStepFlowActive && (
        <DownloadMenuLayout
          {...{
            type,
            documentId,
            isDownloading,
            handleMenuClick: customAction,
            handleDownloadPdfClick: customDownloadAction,
            currentDocument,
            showResumeDistributionModal,
            triggerDownload,
          }}
        />
      )}
    </Topbar>
  )
})
